import type {ExifOrientation} from '../types';
import type {JpegSegment} from './jpeg-segments';

export const APP1_MARKER = 0xe1;
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00] as const;
const TIFF_LITTLE_ENDIAN = 0x4949;
const TIFF_BIG_ENDIAN = 0x4d4d;
const TIFF_MAGIC = 0x002a;
const ORIENTATION_TAG = 0x0112;
const SHORT_TYPE = 3;
const IFD_ENTRY_BYTES = 12;
const ORIENTATIONS: readonly ExifOrientation[] = [1, 2, 3, 4, 5, 6, 7, 8];

const toOrientation = (value: number): ExifOrientation | null =>
  ORIENTATIONS.find(orientation => orientation === value) ?? null;

const readOrientationFromTiff = (tiff: DataView): ExifOrientation | null => {
  if (tiff.byteLength < 8) {
    return null;
  }
  const byteOrder = tiff.getUint16(0);
  const littleEndian = byteOrder === TIFF_LITTLE_ENDIAN;
  if (!littleEndian && byteOrder !== TIFF_BIG_ENDIAN) {
    return null;
  }
  if (tiff.getUint16(2, littleEndian) !== TIFF_MAGIC) {
    return null;
  }
  const ifdOffset = tiff.getUint32(4, littleEndian);
  if (ifdOffset + 2 > tiff.byteLength) {
    return null;
  }
  const entryCount = tiff.getUint16(ifdOffset, littleEndian);
  for (let index = 0; index < entryCount; index += 1) {
    const entry = ifdOffset + 2 + index * IFD_ENTRY_BYTES;
    if (entry + IFD_ENTRY_BYTES > tiff.byteLength) {
      return null;
    }
    if (tiff.getUint16(entry, littleEndian) !== ORIENTATION_TAG) {
      continue;
    }
    if (tiff.getUint16(entry + 2, littleEndian) !== SHORT_TYPE) {
      return null;
    }
    return toOrientation(tiff.getUint16(entry + 8, littleEndian));
  }
  return null;
};

/** Whether an APP1 segment carries Exif (as opposed to XMP). */
export const isExifApp1 = (bytes: Uint8Array, segment: JpegSegment): boolean =>
  segment.marker === APP1_MARKER &&
  EXIF_HEADER.every((value, index) => bytes[segment.dataOffset + index] === value);

/** Orientation (1–8) from an Exif APP1 segment, or `null` when the tag is absent or unreadable. */
export const readOrientationFromExifApp1 = (
  bytes: Uint8Array,
  segment: JpegSegment,
): ExifOrientation | null => {
  const tiffOffset = segment.dataOffset + EXIF_HEADER.length;
  const tiffLength = Math.min(segment.dataLength - EXIF_HEADER.length, bytes.length - tiffOffset);
  if (tiffLength <= 0) {
    return null;
  }
  return readOrientationFromTiff(
    new DataView(bytes.buffer, bytes.byteOffset + tiffOffset, tiffLength),
  );
};
